/**
 * THE AGREEMENT TEXT, BY VERSION (spec §7 item 3; plan §9 item 2).
 *
 * What a firm's administrator reads before agreeing to move the firm's
 * clients onto Cova CRM. The page renders it, the request email quotes its
 * opening, and the agreement row stores the version it was read at
 * (`agreement_version`).
 *
 * VERSION 1 is the lead's draft from decision 002's "What changed", copied
 * verbatim from docs/BMS/planning/cutover/implementation/briefs/
 * agreement-text-v1.md. Tai edits it in the ledger's For Tai section. Once any
 * firm has agreed to "1", a change from Tai is a NEW constant, version "2",
 * beside this one, never an edit to it: an earlier administrator's agreement
 * must keep pointing at the words they read.
 *
 * 2026-10-06: version 1 was edited in place to name the product Cova CRM (Tai).
 * The cutover had reached no production firm, so no real agreement read the
 * earlier words; the next change after a firm agrees is version "2".
 *
 * 2026-10-07: version 1's "What moves." was edited in place to say what the
 * move does with access (insurance_broker_bms.move_org_access, v5.70/036): grants copy at
 * the same level, a client's own team members become personal access, the
 * stronger level wins on a join, and pending invites and the three per-client
 * switches do not carry over. Still no production firm had agreed.
 *
 * 2026-10-25: version 1's "What moves." was edited in place to say Steadfast
 * Apps where it said Insight (Tai: the sync is Steadfast Apps Sync). No
 * production firm can have agreed: BMS has never run on prod, whose deploy
 * failed at v5.09/008 and which still runs 5dea0cd8485.
 *
 * 2026-11-03: VERSION 2 (build plan 2026-11-03-002, Task 3). A production firm
 * agreed to version 1 on 2026-11-02, so version 1 stays verbatim and version 2 is a new
 * constant: every legacy client now gets an answer (join, copy as new, keep
 * together with another, or arrive archived), and staff may give the
 * agreement on the firm's written instruction (decision 059).
 *
 * BROWSER-SAFE: plain data, no imports.
 */

/** The version an agreement written today records. A new wording is a new version. */
export const CURRENT_AGREEMENT_VERSION = "2"

export interface AgreementScopeText {
  readonly label: string
  readonly description: string
}

/** One paragraph of the text: its bold lead and the sentences after it. */
export interface AgreementParagraph {
  readonly lead: string
  readonly body: string
  /** The paragraph the page sets apart as a warning. */
  readonly caution: boolean
}

export interface AgreementText {
  readonly version: string
  /** The page's and the email's heading. */
  readonly title: string
  /** The opening paragraph, which the request email quotes. */
  readonly intro: string
  readonly paragraphs: ReadonlyArray<AgreementParagraph>
  readonly scopes: { readonly recommended: AgreementScopeText; readonly everything: AgreementScopeText }
  /** The confirmation the administrator ticks before the agreement can be given. */
  readonly acknowledgement: string
}

const VERSION_1: AgreementText = {
  version: "1",
  title: "Move your firm to Cova CRM",
  intro:
    "Your firm's client records live today in Clients (Legacy). Cova CRM is your new client system: one record per client, with contacts, notes, important dates, tasks and team access, and a history of every change.",
  paragraphs: [
    {
      lead: "What moves.",
      body: "Your clients, their contacts and addresses, their notes and note tags, their important dates and renewal reminders, and their tasks. If your firm is synced with Steadfast Apps, your legacy clients that match a client already in Cova CRM are joined to that client, and you decide every match below before you agree. Who can see each client moves with it: every team and every person given a client keeps it at the same level, and people on a client's own team keep it as their own access. Where two legacy clients join one, each team and person keeps the stronger of their two levels. Pending invites to a client's team, and the edit, delete and manage team switches on its members, do not carry over. Everything that moves keeps its history.",
      caution: false,
    },
    {
      lead: "What stays behind, read-only.",
      body: "Policies, files, conversations, messages, emails and memory facts stay where they are. You can still open and read them from the client's History, and nothing is deleted. New policies and files are created in Cova CRM from now on.",
      caution: false,
    },
    {
      lead: "What changes the moment the move completes.",
      body: "The old client screens become read-only for everyone at your firm. Anyone adding or editing a client, a note or a task does it in Cova CRM. The agent, imports and automatic rules follow the same rule.",
      caution: false,
    },
    {
      lead: "During the move.",
      body: "While the move is running, your firm's legacy client records cannot be edited. The move usually takes a few seconds; a large firm takes under a minute.",
      caution: false,
    },
    {
      lead: "This cannot be undone.",
      body: "Once your firm has moved, it stays in Cova CRM. There is no way back to Clients (Legacy) as a working system, only as read-only history. If the move fails part way, nothing is lost: the firm stays on the legacy side, and Cova support fixes the cause and runs it again against this same agreement.",
      caution: true,
    },
    {
      lead: "Your choice of scope.",
      body: "Recommended moves what Cova CRM is built for and leaves the rest as read-only history. Everything also moves the records Cova CRM can hold but does not need, and shows the legacy policies list inside Cova CRM.",
      caution: false,
    },
    {
      lead: "Who runs it.",
      body: "Cova support runs the move, only after you have agreed here with every match decided. A support person cannot start it on their own.",
      caution: false,
    },
  ],
  scopes: {
    // The labels are the text's; each description is its own sentence of "Your choice of scope".
    recommended: {
      label: "Recommended",
      description: "Recommended moves what Cova CRM is built for and leaves the rest as read-only history.",
    },
    everything: {
      label: "Everything",
      description:
        "Everything also moves the records Cova CRM can hold but does not need, and shows the legacy policies list inside Cova CRM.",
    },
  },
  acknowledgement:
    "I have read this. I understand that the old client screens become read-only when the move completes and that the move cannot be undone.",
}

const VERSION_1_WHAT_MOVES = VERSION_1.paragraphs[0]?.body ?? ""

const VERSION_2: AgreementText = {
  ...VERSION_1,
  version: "2",
  paragraphs: VERSION_1.paragraphs.map((paragraph) => {
    if (paragraph.lead === "What moves.") {
      return {
        ...paragraph,
        body: VERSION_1_WHAT_MOVES.replace(
          "If your firm is synced with Steadfast Apps, your legacy clients that match a client already in Cova CRM are joined to that client, and you decide every match below before you agree.",
          "Every legacy client gets an answer before you agree: join it to a client already in Cova CRM, copy it across as a new client, keep it together with another of your legacy clients as one client, or bring it across archived, hidden from day-to-day work but kept with its documents and history.",
        ),
      }
    }
    if (paragraph.lead === "Who runs it.") {
      return {
        ...paragraph,
        body: "Cova support runs the move, only after this agreement is given with every client answered. A support person cannot start it on their own.",
      }
    }
    return paragraph
  }),
}

const TEXTS: Readonly<Record<string, AgreementText>> = { "1": VERSION_1, "2": VERSION_2 }

/** The text an agreement at `version` was given against; undefined for a version that never existed. */
export const agreementTextFor = (version: string): AgreementText | undefined => TEXTS[version]

/** The text an administrator reads today. */
export const CURRENT_AGREEMENT_TEXT: AgreementText = VERSION_2
